import { Link, useRouter, type ErrorComponentProps } from '@tanstack/react-router';
import { useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';
import { useTranslation } from '@/lib/i18n/react';
import { Button } from '@/components/ui/Button';

/** Keep route failures in the personal language and hide internal diagnostics. */
export function RouteFailure({ reset }: ErrorComponentProps) {
  const { t } = useTranslation();
  const router = useRouter();
  const queries = useQueryClient();
  const [pending, setPending] = useState(false);
  const retry = async () => {
    if (pending) return;
    setPending(true);
    try {
      // A render failure can come from cached read data. Reset queries before
      // remounting the route; resetting its boundary alone repeats that failure.
      // Mutation state is separate and is never reset or replayed here.
      await queries.resetQueries();
      await router.invalidate();
    } catch {
      // A failed read remains recoverable here; never render its private prose.
    } finally {
      setPending(false);
      reset();
    }
  };
  return (
    <main className="flex min-h-screen items-center justify-center bg-paper p-6 text-fg-1">
      <section className="w-full max-w-lg rounded-md border border-border-subtle bg-white p-6" role="alert">
        <h1 className="font-display text-xl font-bold">{t('routeError.title')}</h1>
        <p className="mt-3 text-sm text-fg-2">{t('routeError.description')}</p>
        <div className="mt-5 flex flex-wrap items-center gap-4">
          <Button onClick={() => void retry()} isLoading={pending}>{t('retry')}</Button>
          <Link to="/" className="inline-flex min-h-11 items-center text-sm font-semibold text-blaze-violet underline">{t('routeError.home')}</Link>
        </div>
      </section>
    </main>
  );
}
